import React, { useState } from 'react';
import { Button, Row, Col, Card } from 'react-bootstrap';
import { Formik, Form, FormikHelpers } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FormGroupCheck } from '@components/Base';
import { PaymentMethods } from '@models/PaymentMethod';
import { PaymentMethodToggleControl } from '@components/Feature/PaymentMethod';
import { routePrivacyPolicyPage } from '@components/PrivacyPolicyPage';
import { routeTermsAndConditionsPage } from '@components/TermsAndConditionsPage';
import { PaymentTransaction } from '@models/PaymentTransaction';
import FakePayment from '@components/FakePayment';
import AbaPayway from '@components/AbaPayway';

import { useFormValues, useValidationSchema } from './hook';
import { FormValues } from './type';

type Props = {
  onSubmit: (values: FormValues) => void;
  paymentTransaction?: PaymentTransaction | null;
};

const HealthQuotePaymentForm = ({ onSubmit, paymentTransaction }: Props) => {
  const { t } = useTranslation();
  const initialValues = useFormValues();
  const validationSchema = useValidationSchema();
  const [bypass, setBypass] = useState(false);
  const PaymentGateway = bypass ? FakePayment : AbaPayway;

  const handleSubmit = (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    onSubmit(values);
    setSubmitting(false);
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnMount
      >
        {({ isValid }) => (
          <Form>
            <Card.Title>{t('paymentOption.title')}</Card.Title>
            <Row className="gy-3 gy-lg-0 mb-5">
              <Col>
                <PaymentMethodToggleControl value={PaymentMethods.AbaPay} />
              </Col>
            </Row>

            <FormGroupCheck
              groupProps={{ className: 'mb-5' }}
              name="agreeToTermsAndConditions"
              label={
                <Trans
                  i18nKey="iAgreeToTermAndPolicy"
                  t={t}
                  components={{
                    term: <Link to={routeTermsAndConditionsPage()} target="_blank" rel="noreferrer" />,
                    policy: <Link to={routePrivacyPolicyPage()} target="_blank" rel="noreferrer" />,
                  }}
                />
              }
            />

            {process.env.RAILS_ENV !== 'production' && (
              <FormGroupCheck
                groupProps={{ className: 'mb-5' }}
                id="bypass"
                name="bypass"
                label="Bypass payment"
                onChange={() => setBypass(true)}
              />
            )}

            <Button type="submit" variant="primary" className="btn-lg w-100" disabled={!isValid}>
              <span className="me-2">{t('common:paySecurely')}</span>
              <FontAwesomeIcon icon={faArrowRight} className="fa-fw" />
            </Button>
          </Form>
        )}
      </Formik>
      {paymentTransaction && <PaymentGateway paymentTransactionId={paymentTransaction.id} />}
    </>
  );
};

export { HealthQuotePaymentForm };
